import type { ReactNode } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormatters } from "@/lib/hooks/useFormatters";
import { useDetailFieldSectionContext } from "../context/DetailFieldSectionContext";
import { formatDetailFieldValue } from "../utils/formatDetailFieldValue";
import { getDetailFieldLabel } from "../utils/getDetailFieldLabel";
import { getDetailFieldValue } from "../utils/getDetailFieldValue";

export type UseDetailFieldOptions =
  | {
      field: string;
      value?: ReactNode;
      label: ReactNode;
      labelKey?: never;
    }
  | {
      field: string;
      value?: ReactNode;
      label?: never;
      labelKey?: string;
    };

interface UseDetailFieldResult {
  value: ReactNode;
  label: ReactNode;
}

export function useDetailField(
  props: UseDetailFieldOptions,
): UseDetailFieldResult {
  const { field, value: valueProp } = props;
  const { t } = useTranslation();
  const { data, schema, module } = useDetailFieldSectionContext();
  const formatters = useFormatters();

  return useMemo(() => {
    const hasCustomValue = valueProp !== undefined;
    const rawValue = getDetailFieldValue(data, field);
    const value = hasCustomValue
      ? valueProp
      : formatDetailFieldValue(schema, field, rawValue, formatters);
    const label =
      "label" in props
        ? props.label
        : getDetailFieldLabel(
            "labelKey" in props ? props.labelKey : undefined,
            module,
            field,
            t,
          );

    return { value, label };
  }, [field, valueProp, props, data, schema, module, t, formatters]);
}
